import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ErrorHandler } from "UI/Errors";
import { Event } from "../../../../../Global/Base/Events";
import { CancellationToken } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { LocalizationUtils } from "../../../../../OW.Remoting/Remoting/LocalizationUtils";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { ActionContext, ActionType } from "../../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../../UI.Controls/Actions/MenuType";
import { ControlParams } from "../../Controls/ControlInterfaces";
import { FormManager } from "../../FormManager/IFormManager";
import { UIManager } from "../../UIManager";
import { IHideButtons } from "../Frames";
import { IMasterableFrame } from "../Frames/FrameInterfaces";
import { ModalWindowType } from "../ModalWindow";
import { OpenModeType } from "./DetailFormAction";
import { XGAction } from "./XGAction";

const logger = LazyLoadingSectionLogger.create(LoggingSection.showInsertAction);

export class ShowInsertAction extends XGAction {
    public readonly executionStarted: Event<ShowInsertAction, { dateFromFld: string; dateToFld: string }> = new Event<ShowInsertAction, { dateFromFld: string; dateToFld: string }>();

    /**
     * Kód formuláře, který se má otevřít pro vytvoření nového záznamu
     */
    public readonly formName: string;

    /**
     * Název třídy nově zakládaného záznamu
     */
    public readonly objectName: string;

    public afterGuiCreateFill: string;

    /**
     * Nastavení tlačítek, která mají být skrytá na hlavním framu
     */
    public hideButtons: IHideButtons;
    public color?: string;
    private _dateFromFld?: string;
    private _dateToFld?: string;
    private _locParams: string[];

    private _insertRightResultPromise?: Promise<boolean>;
    private _insertRightOutdated: boolean;

    public override get context(): ActionContext {
        return ActionContext.frameAction;
    }

    public override get actionType(): ActionType {
        return ActionType.insert;
    }

    public get locParams(): string[] {
        return this._locParams;
    }

    public set locParams(value: string[]) {
        this._locParams = value;
    }

    constructor(params: ControlParams) {
        super(params);
        this.element.classList.add("show-insert-action");
        this.setToMenu(MenuType.mtActions);

        this.formName = this.getDataAttr("formname");
        this.objectName = this.getDataAttr("objectname");
        this.color = this.getDataAttr("color");
        this.locParams = LocalizationUtils.parseLocalizationParams(this.getDataAttr("locparams"));
        this.afterGuiCreateFill = this.element.getAttribute("data-AfterGUICreateFill");

        this._dateFromFld = this.getDataAttr("dateFromFld");
        this._dateToFld = this.getDataAttr("dateToFld");

        if (!this.formName)
            logger.error("Required property FormName is empty!");
        if (!this.objectName)
            logger.error("Required property objectName is empty!");

        const masterObjectChanged = () => this._insertRightOutdated = true;
        this._events.add(this.ownerChanged, () => {
            if (this.owner?.masterFrame) {
                this._events.add(this.owner.masterFrame.actualObjectSaved, masterObjectChanged);
                this._events.add(this.owner.masterFrame.actualObjectChanged, masterObjectChanged);
            }
        });
    }

    protected override async _internalExecute(): Promise<void> {
        this.executionStarted.trigger(this, { dateFromFld: this._dateFromFld, dateToFld: this._dateToFld });
        if (!this.formName || !this.objectName) {
            logger.error("Either property FormName or ObjectName is empty! Action cancelled!");
            return;
        }

        UIManager.instance.showLoader();
        try {
            await FormManager.instance.openFormModal(
                {
                    formName: this.formName,
                    type: ModalWindowType.parametricForm,
                    parametricData: {
                        ownerForm: this.ownerForm,
                        detailFormMasterFrame: this.owner instanceof IMasterableFrame ? this.owner : undefined,
                        autoSelectFirstRow: true,
                        hideWindowActions: true,
                        openMode: OpenModeType.insert,
                        insertObjectName: this.objectName,
                        hideButtons: this.hideButtons,
                        formContext: this.owner?.frameContext,
                        transactionContextGetter: this._useMasterContext ? this.owner?.frameContext?.getTransactionContextId : null,
                        setValues: this.afterGuiCreateFill,
                    },
                });
        } catch (err) {
            ErrorHandler.instance.handleError(err);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    protected _getInsertRight(): Promise<boolean> {
        return this._setInsertRightResultPromise(() =>
            OWManager.getInsertRightWithValidationRights(this.objectName, this.owner.masterObject?.instanceID, this.owner.masterLinkToAttr, this._getRequestContext()).then(r => r.InsertRightValue));
    }

    public evaluateInsertRight(): Promise<boolean | null> {
        if (!this.owner || this.owner.masterFrame && this.owner.masterFrame.actualObject == null) {
            // Aby nebyla SIA aktivní, když není vybraný záznam v masterovi.
            return Promise.resolve(false);
        }

        if (!this.owner.interactive) {
            return Promise.resolve(false);
        }

        return this._getInsertRight();
    }

    private _setInsertRightResultPromise(getInsertRightFunction: (ctf?: CancellationToken) => Promise<boolean>, ct?: CancellationToken): Promise<boolean> {
        if (!this.owner)
            this._insertRightResultPromise = Promise.resolve(false);

        if (this.owner.masterFrame && this.owner.masterFrame.actualObject == null)
            this._insertRightResultPromise = Promise.resolve(false);

        if (!ct?.cancellationRequested && (this._insertRightResultPromise === undefined || this._insertRightOutdated)) {
            this._insertRightResultPromise = getInsertRightFunction(ct);
            this._insertRightOutdated = false;
        }
        return this._insertRightResultPromise;
    }
}
